<template>
    <div class="carbon-dashboard" :class="{ 'carbon-dashboard--empty': showEmpty }">
        <!-- 최초 로딩: 풀스크린 로딩 placeholder -->
        <div v-if="showInitialLoading" class="dashboard-loading" aria-busy="true" aria-live="polite">
            <div class="dashboard-loading__frame">
                <div class="dashboard-loading__topline">
                    <div class="dashboard-loading__brand" aria-hidden="true"></div>
                    <div class="dashboard-loading__status">
                        <span class="dashboard-loading__spinner" aria-hidden="true"></span>
                        <span class="dashboard-loading__status-text">데이터를 불러오는 중…</span>
                    </div>
                </div>
                <div class="dashboard-loading__hint">선택한 기간·조직 기준으로 대시보드를 구성하고 있습니다.</div>

                <div class="dashboard-skeleton">
                    <div class="sk sk--kpi">
                        <div class="sk-line sk-line--w40"></div>
                        <div class="sk-line sk-line--w65"></div>
                    </div>
                    <div class="sk-grid sk-grid--row2">
                        <div class="sk-card">
                            <div class="sk-line sk-line--w35"></div>
                            <div class="sk-block"></div>
                        </div>
                        <div class="sk-card">
                            <div class="sk-line sk-line--w45"></div>
                            <div class="sk-block"></div>
                        </div>
                        <div class="sk-card">
                            <div class="sk-line sk-line--w30"></div>
                            <div class="sk-block"></div>
                        </div>
                    </div>
                    <div class="sk-grid sk-grid--row3">
                        <div class="sk-card">
                            <div class="sk-line sk-line--w50"></div>
                            <div class="sk-block sk-block--tall"></div>
                        </div>
                        <div class="sk-card">
                            <div class="sk-line sk-line--w40"></div>
                            <div class="sk-list">
                                <div class="sk-pill" v-for="i in 5" :key="i"></div>
                            </div>
                        </div>
                        <div class="sk-card">
                            <div class="sk-line sk-line--w55"></div>
                            <div class="sk-block sk-block--tall"></div>
                        </div>
                    </div>
                    <div class="sk-grid sk-grid--row3">
                        <div class="sk-card">
                            <div class="sk-line sk-line--w50"></div>
                            <div class="sk-block sk-block--tall"></div>
                        </div>
                        <div class="sk-card">
                            <div class="sk-line sk-line--w40"></div>
                            <div class="sk-list">
                                <div class="sk-pill" v-for="i in 5" :key="i"></div>
                            </div>
                        </div>
                        <div class="sk-card">
                            <div class="sk-line sk-line--w55"></div>
                            <div class="sk-block sk-block--tall"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 데이터 없음: 풀스크린 placeholder -->
        <div v-else-if="showEmpty" class="dashboard-empty">
            <div class="dashboard-empty">
                <div class="dashboard-empty__inner">
                    <div class="dashboard-empty__block">
                        <div class="dashboard-empty__icon" aria-hidden="true">
                            <img src="/assets/img/empty/emptyBox.png" alt="" class="dashboard-empty__img" />
                        </div>
                        <div class="dashboard-empty__copy">
                            <h2 class="dashboard-empty__title">데이터가 존재하지 않습니다</h2>
                            <p class="dashboard-empty__desc">
                                선택한 기간·조직에 배출량 데이터가 없습니다.<br />다른 조건을 선택하시거나, 데이터가
                                등록된 후 다시 확인해 주세요.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- 데이터 로드 완료 -->
        <template v-else>
            <div v-if="showRefreshingOverlay" class="dashboard-refreshing" aria-hidden="true">
                <div class="dashboard-refreshing__spinner"></div>
                <span class="dashboard-refreshing__text">불러오는 중…</span>
            </div>

            <!-- Hero: 3컬럼 peer metric blocks -->
            <DashboardKpiRow
                :summary="summary"
                :view-mode="viewMode"
                :selected-month="selectedMonth"
                :year-over-year="yearOverYear"
                :target="targetWidget"
                :prev-trend="prevYearTrend"
                :display-unit="displayUnit"
                :cutoff-date="dataCutoffDate"
            />

            <!-- 2행: 월별 추이 (좌) | 사업장 순위 (중) | 감축 목표 (우) -->
            <div class="dashboard-row dashboard-row--trend-fac-tgt">
                <MonthlyTrendChart
                    :view-mode="viewMode"
                    :trend-data="trendChartData"
                    :prev-trend="prevYearTrend"
                    :selected-month="selectedMonth"
                    :cutoff-month="cutoffMonthNum"
                    :cutoff-day="cutoffDayNum"
                    :cutoff-year="Number(selectedYear)"
                    :api-unit="summary.unit || 'kgCO2e'"
                    :display-unit="displayUnit"
                />
                <FacilityEmissionPanel
                    :facilities="facilities"
                    :previous-facilities="previousFacilities"
                    :api-unit="summary.unit || 'kgCO2e'"
                    :display-unit="displayUnit"
                    :yoy-percent="yearOverYear.changeRate || 0"
                />
                <TargetGoalPanel
                    :target="targetWidget"
                    :api-unit="summary.unit || 'kgCO2e'"
                    :display-unit="displayUnit"
                />
            </div>

            <!-- 3행: 산정 워크플로 (좌) | 카테고리 순위 (우) -->
            <div class="dashboard-row dashboard-row--52-48">
                <CategoryTopRank
                    :categories="categoriesWithContribs"
                    :api-unit="summary.unit || 'kgCO2e'"
                    :display-unit="displayUnit"
                />
                <WorkflowTrackPanel
                    :months="inventoryMonths"
                    :prev-year-months="inventoryMonthsPrevYear"
                    :api-unit="summary.unit || 'kgCO2e'"
                    :display-unit="displayUnit"
                />
            </div>
        </template>
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import DashboardKpiRow from "./components/DashboardKpiRow.vue";
import MonthlyTrendChart from "./components/MonthlyTrendChart.vue";
import FacilityEmissionPanel from "./components/FacilityEmissionPanel.vue";
import WorkflowTrackPanel from "./components/WorkflowTrackPanel.vue";
import CategoryTopRank from "./components/CategoryTopRank.vue";
import TargetGoalPanel from "./components/TargetGoalPanel.vue";

export default {
    name: "DashboardMain",
    components: {
        DashboardKpiRow,
        MonthlyTrendChart,
        FacilityEmissionPanel,
        WorkflowTrackPanel,
        CategoryTopRank,
        TargetGoalPanel,
    },
    props: {
        selectedOrgIdx: { type: Number, default: null },
        selectedYear: { type: String, default: () => String(new Date().getFullYear()) },
        selectedMonth: {
            type: String,
            default: () => {
                const d = new Date();
                return String(d.getFullYear()) + String(d.getMonth() + 1).padStart(2, "0");
            },
        },
        viewMode: { type: String, default: "YEAR", validator: (v) => ["YEAR", "MONTH"].includes(v) },
        displayUnit: { type: String, default: "kgCO2e", validator: (v) => ["kgCO2e", "tCO2e"].includes(v) },
    },
    data() {
        return {
            loading: false,
            hasLoaded: false,
            summary: {
                totalEmission: 0,
                scopeBreakdown: { scope1: 0, scope2: 0, scope3: 0 },
                unit: "kgCO2e",
            },
            yearOverYear: { changeRate: null, direction: "none" },
            facilities: [],
            categories: [],
            /** 연간: 월별 추이 (trend.months) */
            trendMonths: [],
            /** 월간: 일별 추이 (trend.days) */
            trendDays: [],
            topSources: [],
            inventoryMonths: [],
            /** 전년 동월 배출 — 워크플로 카드 YoY (widgets.inventory.months, YEAR 조회) */
            inventoryMonthsPrevYear: [],
            inventorySummary: {},
            targetWidget: null,
            /** 전년 월별 실적 — YoY 비교용 [{ m, t }] x12 */
            prevYearTrend: [],
            /** 전년도 사업장 배출 데이터 — FacilityEmissionPanel YoY 계산용 */
            previousFacilities: [],
            /** API _meta.cutoffDate (widgets.facility > category > target) */
            cutoffDate: null,
        };
    },
    computed: {
        isEffectivelyEmpty() {
            const total = Number(this.summary?.totalEmission ?? 0);
            const totalHasValue = Number.isFinite(total) && total > 0;

            const hasFacilities = Array.isArray(this.facilities) && this.facilities.length > 0;
            const hasCategories = Array.isArray(this.categories) && this.categories.length > 0;
            const hasTrend = Array.isArray(this.trendMonths) ? this.trendMonths.length > 0 : false;
            const hasTrendDays = Array.isArray(this.trendDays) ? this.trendDays.length > 0 : false;
            const hasTopSources = Array.isArray(this.topSources) && this.topSources.length > 0;
            const hasInventory = Array.isArray(this.inventoryMonths) && this.inventoryMonths.length > 0;
            const hasTarget = this.targetWidget != null;

            return !(
                totalHasValue ||
                hasFacilities ||
                hasCategories ||
                hasTrend ||
                hasTrendDays ||
                hasTopSources ||
                hasInventory ||
                hasTarget
            );
        },

        showInitialLoading() {
            return this.loading && !this.hasLoaded;
        },

        showEmpty() {
            return this.hasLoaded && !this.loading && this.isEffectivelyEmpty;
        },

        showRefreshingOverlay() {
            return this.loading && this.hasLoaded && !this.showEmpty;
        },

        cutoffMonthNum() {
            const d = this.dataCutoffDate;
            if (!d) return new Date().getMonth() + 1;
            return parseInt(d.split("-")[1], 10);
        },

        dataCutoffDate() {
            return this.cutoffDate || new Date().toISOString().slice(0, 10);
        },

        cutoffDayNum() {
            const d = this.dataCutoffDate;
            if (!d) return new Date().getDate();
            return parseInt(d.split("-")[2], 10);
        },

        /** YEAR: trend.months / MONTH: trend.days */
        trendChartData() {
            return this.viewMode === "MONTH" ? this.trendDays : this.trendMonths;
        },

        categoriesWithContribs() {
            return (this.categories || []).map((c) => ({
                code: c.categoryCode || c.code,
                name: c.categoryName || c.name,
                scope: c.scope,
                scopeType: c.scopeType,
                total: c.total,
                ratio: c.ratio,
                contribs: this.facilitiesForCategory(c.categoryCode || c.code),
            }));
        },
    },
    watch: {
        selectedOrgIdx: {
            handler() {
                this.maybeLoadDashboard();
            },
        },
        selectedYear: {
            handler() {
                this.maybeLoadDashboard();
            },
        },
        selectedMonth: {
            handler() {
                this.maybeLoadDashboard();
            },
        },
        viewMode: {
            handler() {
                this.maybeLoadDashboard();
            },
        },
    },
    mounted() {
        this.maybeLoadDashboard();
    },
    methods: {
        maybeLoadDashboard() {
            if (this._loadScheduled) return;
            this._loadScheduled = true;
            this.$nextTick(() => {
                this._loadScheduled = false;
                if (this.selectedOrgIdx != null && this.selectedYear) {
                    this.loadDashboard();
                }
            });
        },

        onInventorySelectMonth(yyyymm) {
            this.$emit("update:selectedMonth", yyyymm);
        },

        facilitiesForCategory(catCode) {
            const matches = (this.facilities || []).filter((f) => (f.categoryCode || f.category) === catCode);
            const total = matches.reduce((a, f) => a + (f.total || 0), 0) || 1;
            return matches
                .sort((a, b) => (b.total || 0) - (a.total || 0))
                .map((f) => ({
                    name: f.facilityName || f.name,
                    total: f.total,
                    ratio: (f.total / total) * 100,
                }));
        },

        getDashboardOptions() {
            const opts = { viewMode: this.viewMode || "YEAR" };
            if (opts.viewMode === "MONTH" && this.selectedMonth) {
                opts.month = this.selectedMonth;
            }
            return opts;
        },

        yoyDirection(changeRate) {
            if (changeRate == null) return "none";
            if (changeRate > 0) return "increase";
            if (changeRate < 0) return "decrease";
            return "unchanged";
        },

        /** summary + widgets.target → KPI/목표 패널용 (YEAR·MONTH 분기) */
        mapYearOverYear(s, tw, viewMode) {
            const summaryYoY = s.yearOverYear || {};
            const targetYoY = tw?.yearOverYear || {};

            if (viewMode === "MONTH") {
                const sameMonth = targetYoY.sameMonth || summaryYoY.sameMonth;
                const monthOverMonth = tw?.monthOverMonth || summaryYoY.monthOverMonth;
                const changeRate = sameMonth?.changeRate ?? null;
                return {
                    viewMode: "MONTH",
                    changeRate,
                    direction: this.yoyDirection(changeRate),
                    currentYtd: sameMonth?.currentValue ?? s.totalEmission ?? 0,
                    previousYtd: sameMonth?.previousValue ?? 0,
                    monthOverMonth: monthOverMonth || null,
                    sameMonth: sameMonth || null,
                    forecastVsPrevFullYear: null,
                };
            }

            const ytdYoY = targetYoY.ytdVsPrevYtd || summaryYoY.ytdVsPrevYtd;
            const forecastYoY = targetYoY.forecastVsPrevFullYear || summaryYoY.forecastVsPrevFullYear;
            const changeRate = ytdYoY?.changeRate ?? summaryYoY.changeRate ?? null;
            return {
                viewMode: "YEAR",
                changeRate,
                direction: this.yoyDirection(changeRate),
                currentYtd: ytdYoY?.currentYtd ?? s.totalEmission ?? 0,
                previousYtd: ytdYoY?.previousYtd ?? summaryYoY.prevYearTotal ?? 0,
                forecastVsPrevFullYear: forecastYoY || null,
                monthOverMonth: null,
                sameMonth: null,
            };
        },

        /** widgets.target → KPI·목표 패널 공통 flat shape */
        normalizeTargetWidget(tw, viewMode) {
            if (!tw) return null;

            const pathway = tw.pathway || {};
            const strategicTarget = tw.strategicTarget || null;
            const goalAmount = Number(tw.orgTarget?.assignedAmount ?? pathway.targetEmissions) || 0;
            const operationalValue = Number(tw.kpi?.operationalValue);
            const hasOperational = Number.isFinite(operationalValue);
            const forecastBlock = viewMode === "MONTH" ? tw.monthEndForecast : tw.yearEndForecast;

            let achieveRate = tw.orgTarget?.achieveRate ?? pathway.progressRate ?? null;
            if (goalAmount > 0 && hasOperational) {
                achieveRate = Math.round((operationalValue / goalAmount) * 10000) / 100;
            }
            if (viewMode === "MONTH" && forecastBlock?.achieveRate != null) {
                achieveRate = Number(forecastBlock.achieveRate);
            }

            return {
                viewMode,
                pathwayYear: pathway.pathwayYear ?? tw.complianceFlex?.complianceYear ?? strategicTarget?.targetYear,
                targetEmissions: Number(pathway.targetEmissions) || 0,
                goalAmount,
                actualEmissions: Number(pathway.actualEmissions) || 0,
                operationalValue: hasOperational ? operationalValue : null,
                achieveRate: achieveRate != null ? Number(achieveRate) : null,
                forecastValue: forecastBlock?.forecastValue != null ? Number(forecastBlock.forecastValue) : null,
                forecastAchieveRate: forecastBlock?.achieveRate != null ? Number(forecastBlock.achieveRate) : null,
                forecastConfidence: forecastBlock?.confidenceLevel ?? null,
                monthOverMonth: tw.monthOverMonth || null,
                goalStat: pathway.goalStat ?? strategicTarget?.goalStat,
                reductionRate: tw.baseYearComparison?.reductionRate,
                targetReductionRate: tw.baseYearComparison?.targetReductionRate,
                baseEmissions: tw.baseYearComparison?.baseEmissions,
                currentEmissions:
                    tw.baseYearComparison?.currentEmissions ??
                    (hasOperational ? operationalValue : pathway.actualEmissions),
                baseYear: tw.baseYearComparison?.baseYear,
                strategicTarget: strategicTarget
                    ? {
                          targetIdx: strategicTarget.targetIdx,
                          goalName: strategicTarget.goalName,
                          baseYear: strategicTarget.baseYear,
                          targetYear: strategicTarget.targetYear,
                          targetEmissions: Number(strategicTarget.targetEmissions) || 0,
                          goalStat: strategicTarget.goalStat,
                          unit: strategicTarget.unit || "kgCO2e",
                      }
                    : null,
                complianceFlex: tw.complianceFlex || null,
                isDraft: Boolean(tw._meta?.isDraft),
                estimationMode: tw._meta?.estimationMode ?? null,
            };
        },

        /** widgets.trend.comparison.previous → 추이 비교 라인용 */
        normalizePreviousTrend(trendWidget, viewMode) {
            const previous = trendWidget?.comparison?.previous;
            if (!previous) return [];

            if (viewMode === "MONTH") {
                return (previous.days ?? []).map((d) => ({
                    d: d.day ?? (d.period ? parseInt(String(d.period).slice(6, 8), 10) : null),
                    t: d.total ?? 0,
                }));
            }

            return (previous.months ?? []).map((m) => ({
                m: m.month ?? (m.period ? parseInt(String(m.period).slice(4, 6), 10) : null),
                t: m.total ?? 0,
            }));
        },

        /** widgets.inventory.comparison.previous → 워크플로 YoY 계산용 */
        normalizePreviousInventoryMonths(inventoryWidget) {
            return inventoryWidget?.comparison?.previous?.months ?? [];
        },

        async loadDashboard(forceRefresh = false) {
            const orgIdx = this.selectedOrgIdx;
            const year = this.selectedYear;
            if (orgIdx == null || !year) return;

            const options = this.getDashboardOptions();
            if (forceRefresh) {
                options.noCache = true;
            }

            this.loading = true;
            this.$emit("loading", true);
            try {
                const res = await carbonApi.dashboard.getOverview(orgIdx, year, options);
                const payload = res.data?.data != null ? res.data.data : res.data;
                if (!payload) return;

                const s = payload.summary || {};
                const w = payload.widgets || {};

                this.summary = {
                    totalEmission: s.totalEmission ?? 0,
                    scopeBreakdown: s.scopeBreakdown || { scope1: 0, scope2: 0, scope3: 0 },
                    unit: s.unit || "kgCO2e",
                    yearOverYear: s.yearOverYear || null,
                    targetSummary: s.targetSummary || null,
                    inventorySummary: s.inventorySummary || null,
                    complianceFlex: s.complianceFlex || null,
                };

                const tw = w.target;
                const viewMode = options.viewMode || payload._meta?.viewMode || "YEAR";

                this.cutoffDate =
                    tw?._meta?.operationalCoverageCutoff ||
                    w.facility?._meta?.cutoffDate ||
                    w.category?._meta?.cutoffDate ||
                    null;

                this.yearOverYear = this.mapYearOverYear(s, tw, viewMode);
                this.targetWidget = this.normalizeTargetWidget(tw, viewMode);

                this.topSources = w.topSource?.sources ?? w.topSources ?? w.topSourcesDetail?.sources ?? [];

                const trendWidget = w.trend || null;
                if (viewMode === "MONTH") {
                    this.trendMonths = [];
                    this.trendDays = trendWidget?.days ?? [];
                } else {
                    this.trendMonths = trendWidget?.months ?? w.monthlyTrend ?? [];
                    this.trendDays = [];
                }

                const invSummary = w.inventory?.summary;
                this.inventorySummary = invSummary
                    ? {
                          confirmed: invSummary.confirmed ?? invSummary.verified ?? 0,
                          total: invSummary.total ?? 12,
                      }
                    : s.inventorySummary
                      ? { confirmed: s.inventorySummary.confirmed, total: s.inventorySummary.total }
                      : {};

                // facilities: FacilityEmissionPanel이 기대하는 name/code/type/scope 필드로 정규화
                this.facilities = (w.facility?.facilities ?? []).map((f) => ({
                    ...f,
                    name: f.facilityName || f.name,
                    code: f.facilityCode || f.code,
                    type: f.facilityType || f.type,
                    // scope1/2/3 중 가장 큰 값의 scope를 대표 scope로 사용
                    scope:
                        (f.scope2 || 0) >= (f.scope1 || 0) && (f.scope2 || 0) >= (f.scope3 || 0)
                            ? 2
                            : (f.scope1 || 0) >= (f.scope3 || 0)
                              ? 1
                              : 3,
                }));
                this.previousFacilities = w.facility?.comparison?.previous?.facilities ?? [];

                this.categories = w.category?.categories ?? [];
                this.inventoryMonths = w.inventory?.months ?? [];
                this.prevYearTrend = this.normalizePreviousTrend(trendWidget, viewMode);
                this.inventoryMonthsPrevYear = this.normalizePreviousInventoryMonths(w.inventory);

                this.hasLoaded = true;
            } catch (e) {
                console.error("[DashboardMain] 대시보드 로드 실패:", e);
                this.facilities = [];
                this.categories = [];
                this.trendMonths = [];
                this.trendDays = [];
                this.inventoryMonths = [];
                this.inventoryMonthsPrevYear = [];
                this.targetWidget = null;
                this.prevYearTrend = [];
                this.previousFacilities = [];
                this.cutoffDate = null;
                this.hasLoaded = true;
            } finally {
                this.loading = false;
                this.$emit("loading", false);
            }
        },
    },
};
</script>

<style scoped>
.carbon-dashboard {
    /* 대시보드 공통 팔레트 — 하위 패널은 var(--dash-*) 참조 */
    --dash-page-bg: #d9e0e7;
    --dash-surface: #ffffff;
    --dash-surface-2: rgb(244 244 244);
    --dash-hairline: #ddd6c7;
    --dash-hairline-soft: #e8e2d4;
    --dash-ink: #11161a;
    --dash-ink-soft: #4a5560;
    --dash-ink-mute: #8b8f8a;
    --dash-ink-faint: #b6b6ad;
    --dash-accent: oklch(0.5 0.09 168);
    --dash-accent-wash: oklch(0.94 0.025 168);
    --dash-accent-tint: oklch(0.88 0.04 168);
    --dash-warn: oklch(0.66 0.13 70);
    --dash-warn-wash: oklch(0.95 0.03 80);
    --dash-warn-tint: oklch(0.88 0.06 70);
    --dash-scope1: oklch(0.62 0.1 70);
    --dash-scope1-wash: oklch(0.96 0.04 70);
    --dash-scope2: oklch(0.5 0.09 168);
    --dash-scope3: oklch(0.55 0.07 230);
    --dash-scope3-wash: oklch(0.95 0.04 230);
    --dash-base-bar: #d0cdc6;
    --dash-ok-bar: oklch(0.72 0.07 168);

    /* 패널 섹션 제목 (cat / trend / fac / tgp / wf eyebrow 공통) */
    --dash-panel-eyebrow-size: 13px;
    --dash-panel-eyebrow-weight: 600;
    --dash-panel-eyebrow-tracking: 0.02em;
    --dash-panel-eyebrow-color: var(--dash-ink-mute);
    --dash-panel-eyebrow-meta-size: 11px;
    --dash-panel-eyebrow-meta-color: var(--dash-ink-faint);

    background: var(--dash-page-bg);

    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    min-height: 0;
    font-size: 12px;
    box-sizing: border-box;
    overflow: hidden;
}

.carbon-dashboard--empty {
    align-items: stretch;
}

.dashboard-row {
    display: grid;
    gap: 10px;
}
.dashboard-row--62-38 {
    grid-template-columns: 1.7fr 1fr;
}
.dashboard-row--trend-fac-tgt {
    grid-template-columns: 1.35fr 1fr 0.75fr;
    align-items: stretch;
    min-height: 0;
    flex: 1;
}
.dashboard-row--trend-fac-tgt :deep(.trend-wrap),
.dashboard-row--trend-fac-tgt :deep(.fac-wrap),
.dashboard-row--trend-fac-tgt :deep(.tgp) {
    height: 100%;
}
.dashboard-row--52-48 {
    grid-template-columns: 0.3fr 1fr;
    flex: 1;
    align-items: stretch;
    min-height: 0;
}
.dashboard-row--52-48 :deep(.wf-wrap),
.dashboard-row--52-48 :deep(.cat-wrap) {
    height: 100%;
    min-height: 0;
}

/* 최초 로딩 placeholder */
.dashboard-loading {
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    height: 100%;
    width: 100%;
    background:
        radial-gradient(900px 500px at 18% 18%, rgba(79, 70, 229, 0.12), transparent 55%),
        radial-gradient(900px 550px at 82% 24%, rgba(5, 150, 105, 0.12), transparent 52%),
        linear-gradient(165deg, #f8fafc 0%, #f1f5f9 45%, #e2e8f0 100%);
    border-radius: 12px;
}
.dashboard-loading__frame {
    width: 100%;
    height: 100%;
    padding: 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.62);
    box-shadow:
        0 6px 18px rgba(15, 23, 42, 0.08),
        inset 0 0 0 1px rgba(148, 163, 184, 0.25);
    backdrop-filter: blur(6px);
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
}
.dashboard-loading__topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
.dashboard-loading__brand {
    width: 140px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
        90deg,
        rgba(79, 70, 229, 0.85) 0%,
        rgba(2, 132, 199, 0.8) 45%,
        rgba(5, 150, 105, 0.8) 100%
    );
    opacity: 0.6;
}
.dashboard-loading__status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(248, 250, 252, 0.9);
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.28);
}
.dashboard-loading__spinner {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid rgba(100, 116, 139, 0.25);
    border-top-color: rgba(15, 23, 42, 0.55);
    animation: dashboardSpin 0.75s linear infinite;
}
.dashboard-loading__status-text {
    font-size: 11px;
    font-weight: 650;
    color: #334155;
    letter-spacing: -0.01em;
}
.dashboard-loading__hint {
    font-size: 11px;
    color: #64748b;
    letter-spacing: -0.01em;
}

/* skeleton */
.dashboard-skeleton {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 9px;
}
.sk {
    border-radius: 10px;
    background: rgba(248, 250, 252, 0.92);
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.22);
}
.sk--kpi {
    height: 74px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.sk-grid {
    display: grid;
    gap: 10px;
    min-height: 0;
}
.sk-grid--row2 {
    grid-template-columns: 1.2fr 1fr 1fr;
    min-height: 0;
}
.sk-grid--row3 {
    grid-template-columns: 1.2fr 1fr 1fr;
    min-height: 0;
    flex: 1;
}
.sk-card {
    border-radius: 10px;
    background: rgba(248, 250, 252, 0.92);
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.22);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
}
.sk-line {
    height: 10px;
    border-radius: 999px;
    position: relative;
    overflow: hidden;
    background: rgba(226, 232, 240, 0.95);
}
.sk-line--w30 {
    width: 30%;
}
.sk-line--w35 {
    width: 35%;
}
.sk-line--w40 {
    width: 40%;
}
.sk-line--w45 {
    width: 45%;
}
.sk-line--w50 {
    width: 50%;
}
.sk-line--w55 {
    width: 55%;
}
.sk-line--w65 {
    width: 65%;
}
.sk-block {
    flex: 1;
    border-radius: 10px;
    position: relative;
    overflow: hidden;
    background: rgba(226, 232, 240, 0.95);
}
.sk-block--tall {
    min-height: 220px;
}
.sk-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
}
.sk-pill {
    height: 34px;
    border-radius: 10px;
    position: relative;
    overflow: hidden;
    background: rgba(226, 232, 240, 0.95);
}
.sk-line::before,
.sk-block::before,
.sk-pill::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
        110deg,
        rgba(226, 232, 240, 0) 0%,
        rgba(248, 250, 252, 0.9) 45%,
        rgba(226, 232, 240, 0) 90%
    );
    transform: translateX(-60%);
    animation: dashboardShimmer 1.1s ease-in-out infinite;
}
@keyframes dashboardShimmer {
    to {
        transform: translateX(60%);
    }
}

/* 로딩 중(리프레시) 오버레이: 기존 데이터 유지 */
.dashboard-refreshing {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow:
        0 6px 18px rgba(15, 23, 42, 0.08),
        inset 0 0 0 1px rgba(148, 163, 184, 0.25);
    backdrop-filter: blur(6px);
    pointer-events: none;
}
.dashboard-refreshing__spinner {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid rgba(100, 116, 139, 0.25);
    border-top-color: rgba(15, 23, 42, 0.55);
    animation: dashboardSpin 0.75s linear infinite;
}
.dashboard-refreshing__text {
    font-size: 11px;
    font-weight: 600;
    color: #334155;
    letter-spacing: -0.01em;
}

@keyframes dashboardSpin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

/* 데이터 없음 placeholder: 화면 꽉 차게, 현대적·고급스러운 톤 */
.dashboard-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    background: linear-gradient(165deg, #f8fafc 0%, #f1f5f9 45%, #e2e8f0 100%);
    border-radius: 12px;
}

.dashboard-empty__inner {
    text-align: center;
}

.dashboard-empty__block {
    position: relative;
    display: inline-block;
}

.dashboard-empty__icon {
    color: #64748b;
    opacity: 0.95;
}

.dashboard-empty__icon .dashboard-empty__img {
    display: block;
    max-width: 650px;
    max-height: 450px;
    margin: 0 auto;
}

.dashboard-empty__copy {
    position: absolute;

    top: 71%;
    left: 5%;
    text-align: center;
}

.dashboard-empty__title {
    font-size: clamp(22px, 3.2vw, 28px);
    font-weight: 600;
    color: #388e3c;
    letter-spacing: -0.03em;
    margin: 0 0 12px 0;
    line-height: 1.4;
}

.dashboard-empty__desc {
    font-size: 15px;
    font-weight: 400;
    color: #64748b;
    line-height: 1.65;
    margin: 0;
}
</style>

<!-- 하위 패널 공통 eyebrow (--dash-panel-eyebrow-* 는 .carbon-dashboard 에서 상속) -->
<style>
.carbon-dashboard .dash-panel-eyebrow {
    font-size: var(--dash-panel-eyebrow-size, 12px);
    font-weight: var(--dash-panel-eyebrow-weight, 600);
    letter-spacing: var(--dash-panel-eyebrow-tracking, 0.02em);
    color: var(--dash-panel-eyebrow-color, #8b8f8a);
    flex-shrink: 0;
}
.carbon-dashboard .dash-panel-eyebrow-meta {
    font-size: var(--dash-panel-eyebrow-meta-size, 11px);
    color: var(--dash-panel-eyebrow-meta-color, #b6b6ad);
    margin-left: auto;
    flex-shrink: 0;
}
</style>
